import type { Color, Config, LinkRuleKind } from '@ml/config';
import { compareRowMajor, getCell, inBounds, isBlocked, sameCoord } from './grid.ts';
import type { Board, Cell, Coord, SeqToken } from './types.ts';

export type LinkRule = Config['link']['rules'][LinkRuleKind];

/** Phần config mà luật nối cần (để test có thể dựng gọn). */
export type LinkConfig = Pick<Config, 'link'>;

/**
 * Ngữ cảnh lọc tiền tố (orbs.md 3.2): các dãy màu "đang dùng được" — dãy skill không hồi
 * chiêu, và dãy ult nếu ult đầy. Dãy đơn sắc luôn được tính, không cần khai.
 */
export type LinkContext = {
  readonly patterns: readonly (readonly Color[])[];
};
export const NO_PATTERNS: LinkContext = { patterns: [] };

/** Khoá `link.rules.<kind>` của một viên. */
export function ruleKindOf(cell: Cell): LinkRuleKind {
  switch (cell.t) {
    case 'orb':
      return cell.kind;
    case 'booster':
      return 'booster';
    case 'lightball':
      return 'lightball';
    case 'essence':
      return 'essence';
  }
}

export function ruleOf(cell: Cell, config: LinkConfig): LinkRule {
  return config.link.rules[ruleKindOf(cell)];
}

/** Màu tính vào `seq`: orb/booster → màu; light ball → `*`; ngọc ấn → null (không tính). */
export function seqTokenOf(cell: Cell): SeqToken | null {
  if (cell.t === 'orb' || cell.t === 'booster') return cell.color;
  if (cell.t === 'lightball') return '*';
  return null;
}

export function isBoosterLike(cell: Cell | null): boolean {
  return !!cell && (cell.t === 'booster' || cell.t === 'lightball');
}

/** Điều kiện hình học (orbs.md 2.1). */
export function inRuleRange(
  rule: Pick<LinkRule, 'range' | 'axis'>,
  dx: number,
  dy: number,
): boolean {
  if (dx === 0 && dy === 0) return false;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const r = rule.range;
  switch (rule.axis) {
    case 'any':
      return Math.max(ax, ay) <= r;
    case 'ortho':
      return (dx === 0 || dy === 0) && ax + ay <= r;
    case 'h':
      return dy === 0 && ax >= 1 && ax <= r;
    case 'v':
      return dx === 0 && ay >= 1 && ay <= r;
    case 'diag':
      return ax === ay && ax >= 1 && ax <= r;
  }
}

/** Bộ lọc của viên cuối với viên đích (orbs.md 2.1). */
export function passesFilter(rule: Pick<LinkRule, 'filter'>, tail: Cell, target: Cell): boolean {
  switch (rule.filter) {
    case 'any':
      return true;
    case 'sameColor': {
      if (target.t === 'lightball') return true;
      const tc = seqTokenOf(target);
      if (tc === null) return false; // ngọc ấn không có màu
      const own = seqTokenOf(tail);
      // viên cuối là light ball/ngọc ấn mà khai sameColor: chỉ còn khớp được light ball
      return own !== null && own !== '*' && own === tc;
    }
    case 'sameKind':
      return target.t === tail.t && ruleKindOf(target) === ruleKindOf(tail);
  }
}

/** `p` khớp tiền tố với `seq` (wildcard `*` trong seq khớp mọi màu). */
export function isPrefixOf(seq: readonly SeqToken[], pattern: readonly Color[]): boolean {
  if (seq.length > pattern.length) return false;
  for (let i = 0; i < seq.length; i++) {
    const s = seq[i];
    if (s !== '*' && s !== pattern[i]) return false;
  }
  return true;
}

/** Mọi phần tử không phải `*` cùng một màu (hoặc rỗng). */
export function isMonochrome(seq: readonly SeqToken[]): boolean {
  let c: SeqToken | undefined;
  for (const s of seq) {
    if (s === '*') continue;
    if (c === undefined) c = s;
    else if (c !== s) return false;
  }
  return true;
}

/** Lọc tiền tố khả thi: seq là tiền tố của một dãy đơn sắc, hoặc của một dãy trong ctx. */
export function isFeasiblePrefix(seq: readonly SeqToken[], ctx: LinkContext): boolean {
  return isMonochrome(seq) || ctx.patterns.some((p) => isPrefixOf(seq, p));
}

export function seqOfPath(board: Board, path: readonly Coord[]): SeqToken[] {
  const seq: SeqToken[] = [];
  for (const c of path) {
    const cell = getCell(board, c.x, c.y);
    const t = cell ? seqTokenOf(cell) : null;
    if (t !== null) seq.push(t);
  }
  return seq;
}

/** Viên làm được đầu chuỗi: có viên, `canStart`, không khoá/băng. */
export function canStartAt(board: Board, c: Coord, config: LinkConfig): boolean {
  if (!inBounds(board, c.x, c.y)) return false;
  const cell = getCell(board, c.x, c.y);
  return !!cell && ruleOf(cell, config).canStart && !isBlocked(cell);
}

function candidateCoords(board: Board, tail: Coord, rule: LinkRule): Coord[] {
  // Mọi ô trong ô vuông bán kính range quanh tail, theo thứ tự hàng, rồi lọc hình học.
  const out: Coord[] = [];
  const r = rule.range;
  for (let y = Math.max(0, tail.y - r); y <= Math.min(board.height - 1, tail.y + r); y++) {
    for (let x = Math.max(0, tail.x - r); x <= Math.min(board.width - 1, tail.x + r); x++) {
      if (inRuleRange(rule, x - tail.x, y - tail.y)) out.push({ x, y });
    }
  }
  return out;
}

/**
 * Ứng viên nối (core-loop 5.2). Chuỗi rỗng → mọi viên làm được đầu chuỗi. Ngược lại ô `c`
 * là ứng viên khi đủ: (1) trong tầm luật của viên cuối, (2) qua filter của viên cuối,
 * (3) chưa trong chuỗi, `canChain`, không khoá/băng, (4) lọc tiền tố nếu `link.prefixFilter`.
 * (5) Ngoại lệ combo (review s1 E2): chỉ khi `path` là đúng 1 viên booster/light ball thì
 * booster/light ball khác qua (1)–(3) là ứng viên bất kể (4), nếu `link.boosterComboAnyAdjacency`.
 * Kết quả theo thứ tự hàng. Không kiểm `path` hợp lệ (dùng `validatePath`).
 */
export function getLinkCandidates(
  board: Board,
  path: readonly Coord[],
  config: LinkConfig,
  ctx: LinkContext = NO_PATTERNS,
): Coord[] {
  if (path.length === 0) {
    const out: Coord[] = [];
    for (let y = 0; y < board.height; y++) {
      for (let x = 0; x < board.width; x++)
        if (canStartAt(board, { x, y }, config)) out.push({ x, y });
    }
    return out;
  }
  const tailCoord = path[path.length - 1]!;
  const tail = getCell(board, tailCoord.x, tailCoord.y);
  if (!tail) return [];
  const rule = ruleOf(tail, config);
  const seq = seqOfPath(board, path);
  const comboException =
    config.link.boosterComboAnyAdjacency && path.length === 1 && isBoosterLike(tail);

  const out: Coord[] = [];
  for (const c of candidateCoords(board, tailCoord, rule)) {
    const cell = getCell(board, c.x, c.y);
    if (!cell) continue;
    if (path.some((p) => sameCoord(p, c))) continue;
    if (!ruleOf(cell, config).canChain || isBlocked(cell)) continue;
    if (!passesFilter(rule, tail, cell)) continue;
    if (config.link.prefixFilter && !(comboException && isBoosterLike(cell))) {
      const tok = seqTokenOf(cell);
      if (tok !== null && !isFeasiblePrefix([...seq, tok], ctx)) continue;
    }
    out.push(c);
  }
  return out.sort(compareRowMajor);
}

export type PathValidation =
  | { readonly ok: true }
  | {
      readonly ok: false;
      /** Chỉ số viên đầu tiên làm chuỗi hỏng. */
      readonly index: number;
      readonly reason: 'empty' | 'outOfBounds' | 'emptyCell' | 'cannotStart' | 'notCandidate';
    };

/**
 * Kiểm cả chuỗi dựng được bằng đúng các bước nối hợp lệ: viên đầu làm được đầu chuỗi,
 * mỗi viên sau là ứng viên của tiền tố trước nó (cùng config/ctx với lúc chọn).
 * Không phân loại đòn (s5 làm).
 */
export function validatePath(
  board: Board,
  path: readonly Coord[],
  config: LinkConfig,
  ctx: LinkContext = NO_PATTERNS,
): PathValidation {
  if (path.length === 0) return { ok: false, index: 0, reason: 'empty' };
  for (let i = 0; i < path.length; i++) {
    const c = path[i]!;
    if (!inBounds(board, c.x, c.y)) return { ok: false, index: i, reason: 'outOfBounds' };
    if (!getCell(board, c.x, c.y)) return { ok: false, index: i, reason: 'emptyCell' };
  }
  if (!canStartAt(board, path[0]!, config)) return { ok: false, index: 0, reason: 'cannotStart' };
  for (let i = 1; i < path.length; i++) {
    const cands = getLinkCandidates(board, path.slice(0, i), config, ctx);
    if (!cands.some((c) => sameCoord(c, path[i]!))) {
      return { ok: false, index: i, reason: 'notCandidate' };
    }
  }
  return { ok: true };
}

/**
 * Ngón tay đi vào ô `c` khi kéo (core-loop 5.1): ứng viên → nối; viên áp chót và
 * `link.allowBacktrack` → gỡ viên cuối; còn lại → giữ nguyên. Chuỗi rỗng: bắt đầu nếu
 * `c` làm được đầu chuỗi. Trả chuỗi mới (mảng mới khi đổi, chính `path` khi không đổi).
 */
export function dragEnter(
  board: Board,
  path: readonly Coord[],
  c: Coord,
  config: LinkConfig,
  ctx: LinkContext = NO_PATTERNS,
): readonly Coord[] {
  if (path.length === 0) return canStartAt(board, c, config) ? [c] : path;
  if (config.link.allowBacktrack && path.length >= 2 && sameCoord(path[path.length - 2]!, c)) {
    return path.slice(0, -1);
  }
  if (getLinkCandidates(board, path, config, ctx).some((k) => sameCoord(k, c))) return [...path, c];
  return path;
}
